import "@/styles/animations.scss";
import "@/styles/giftcard.scss";
import "@/styles/global.scss";
import "@/styles/interaction.scss";
import "@/styles/product-card.scss";

import { apiPlugin, storyblokInit } from "@storyblok/react";
import type { DehydratedState } from "@tanstack/react-query";
import {
  Hydrate,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import type { NextPageContext } from "next";
import type { AppProps } from "next/app";
import { useRouter } from "next/router";
import type { AbstractIntlMessages } from "next-intl";
import { NextIntlClientProvider } from "next-intl";
import { DefaultSeo } from "next-seo";
import React from "react";

import { components } from "@/bloks/Bloks";
import type { ISiteSettingsBlok } from "@/bloks/SiteSettings/SiteSettings";
import Layout from "@/components/Layout";
import Device from "@/components/tools/Device";
import LangRedirection from "@/components/tools/LangRedirection";
import Toolkit from "@/components/tools/Toolkit";
import type { LocalePaths } from "@/features/i18n/LocalePathContext";
import { LocalePathProvider } from "@/features/i18n/LocalePathContext";
import Analytics from "@/lib/Analytics";

type PageProps = {
  dehydratedState?: DehydratedState;
  messages?: AbstractIntlMessages;
  siteSettings?: ISiteSettingsBlok | null;
  localePaths?: LocalePaths | null;
};

type ExtendedAppProps<P = {}> = {
  err?: NextPageContext["err"];
} & AppProps<P>;

storyblokInit({
  accessToken: process.env.NEXT_PUBLIC_STORYBLOK_TOKEN,
  use: [apiPlugin],
  components,
});

const timeZone = "Europe/Stockholm";

function App({ Component, pageProps }: ExtendedAppProps<PageProps>) {
  const [queryClient] = React.useState(() => new QueryClient());
  const router = useRouter();

  if (
    router.route === "/statics/[static]" ||
    router.route === "/no/statics/[static]" ||
    router.route === "/en/statics/[static]"
  ) {
    return (
      <NextIntlClientProvider
        messages={pageProps.messages}
        locale={router.locale}
        timeZone={timeZone}
      >
        <QueryClientProvider client={queryClient}>
          <Hydrate state={pageProps.dehydratedState}>
            <DefaultSeo
              titleTemplate="Røros Tweed | %s"
              defaultTitle="Røros Tweed"
            />
            <Toolkit>
              <Device>
                <Component {...pageProps} />
              </Device>
            </Toolkit>
          </Hydrate>
          <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
      </NextIntlClientProvider>
    );
  }

  return (
    <NextIntlClientProvider
      messages={pageProps.messages}
      locale={router.locale}
      timeZone={timeZone}
    >
      <QueryClientProvider client={queryClient}>
        <Hydrate state={pageProps.dehydratedState}>
          <DefaultSeo
            titleTemplate="Røros Tweed | %s"
            defaultTitle="Røros Tweed"
          />
          <LocalePathProvider value={pageProps.localePaths ?? null}>
            <Toolkit>
              <Device>
                <Analytics />
                <Layout siteSettings={pageProps.siteSettings ?? null}>
                  <Component {...pageProps} />
                  <LangRedirection />
                </Layout>
              </Device>
            </Toolkit>
          </LocalePathProvider>
        </Hydrate>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </NextIntlClientProvider>
  );
}

export default App;
